import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Loader2, UserPlus, Copy, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY_INVITE = { name: "", email: "", designation: "", department: "", contact_number: "" };

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-800",
    accepted: "bg-green-100 text-green-800",
    revoked: "bg-slate-100 text-slate-600",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

export default function EnterpriseBuyers() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_INVITE);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/enterprise/users").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const invite = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/enterprise/buyers/invite", form);
      toast.success(`Invitation sent to ${form.email}`);
      setOpen(false);
      setForm(EMPTY_INVITE);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/accept-invitation/${token}`);
    toast.success("Invitation link copied");
  };

  const revoke = async (id) => {
    try {
      await api.post(`/enterprise/invitations/${id}/revoke`);
      toast.success("Invitation revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { buyers, technical_reviewers, approvers, invitations } = data;

  return (
    <div className="space-y-8" data-testid="enterprise-buyers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Buyers & Team</h1>
          <p className="mt-1 text-sm text-muted-foreground">Invite buyers to your procurement workspace and review your extended team.</p>
        </div>
        <Button data-testid="invite-buyer-btn" className="bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" /> Invite Buyer
        </Button>
      </div>

      <section>
        <h2 className="mb-3 font-heading text-lg font-semibold">Buyers ({buyers.length})</h2>
        <div className="overflow-x-auto rounded-lg border bg-white" data-testid="buyers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Designation</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {buyers.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No buyers yet. Invite your first buyer to get started.</td></tr>
              )}
              {buyers.map((b) => (
                <tr key={b.id} className="border-b last:border-0" data-testid={`buyer-row-${b.email}`}>
                  <td className="px-4 py-3 font-medium">{b.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{b.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{b.designation || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{b.department || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.active === false ? "revoked" : "accepted"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-3 font-heading text-lg font-semibold">Invitations ({invitations.length})</h2>
        <div className="overflow-x-auto rounded-lg border bg-white" data-testid="invitations-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Invited</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No invitations sent yet.</td></tr>
              )}
              {invitations.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0" data-testid={`invitation-row-${inv.email}`}>
                  <td className="px-4 py-3 font-medium">{inv.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(inv.created_at)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {inv.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" data-testid={`copy-invite-${inv.email}`} onClick={() => copyLink(inv.token)}>
                          <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
                        </Button>
                        <Button variant="outline" size="sm" data-testid={`revoke-invite-${inv.email}`}
                          className="text-red-600 hover:text-red-700" onClick={() => revoke(inv.id)}>
                          <Ban className="mr-1 h-3.5 w-3.5" /> Revoke
                        </Button>
                      </div>
                    )}
                    {inv.status === "accepted" && <CheckCircle2 className="ml-auto h-4 w-4 text-green-600" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-3 font-heading text-lg font-semibold">Technical Reviewers ({technical_reviewers.length})</h2>
          <div className="rounded-lg border bg-white" data-testid="reviewers-list">
            {technical_reviewers.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">None yet — buyers can add technical reviewers from their workspace.</p>}
            {technical_reviewers.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b px-4 py-3 last:border-0" data-testid={`reviewer-row-${r.email}`}>
                <div>
                  <p className="text-sm font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.email} · {r.designation}</p>
                </div>
                <StatusBadge status={r.status === "active" ? "accepted" : "revoked"} />
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="mb-3 font-heading text-lg font-semibold">Approvers ({approvers.length})</h2>
          <div className="rounded-lg border bg-white" data-testid="approvers-list">
            {approvers.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">None yet — buyers can add approvers from their workspace.</p>}
            {approvers.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b px-4 py-3 last:border-0" data-testid={`approver-row-${a.email}`}>
                <div>
                  <p className="text-sm font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.email} · {a.designation}</p>
                </div>
                <StatusBadge status={a.status === "active" ? "accepted" : "revoked"} />
              </div>
            ))}
          </div>
        </section>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="invite-buyer-dialog">
          <DialogHeader><DialogTitle>Invite a Buyer</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The buyer receives a secure link to set their own password. Invitations expire after 72 hours.</p>
          <form onSubmit={invite} className="mt-2 space-y-4">
            <div>
              <Label htmlFor="ib-name">Name *</Label>
              <Input id="ib-name" data-testid="invite-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ib-email">Email ID *</Label>
              <Input id="ib-email" data-testid="invite-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ib-desig">Designation</Label>
                <Input id="ib-desig" data-testid="invite-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="ib-dept">Department</Label>
                <Input id="ib-dept" data-testid="invite-department" value={form.department} onChange={(e) => set("department", e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="ib-phone">Contact Number</Label>
              <Input id="ib-phone" data-testid="invite-contact" value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} className="mt-1.5" />
            </div>
            {error && <p data-testid="invite-form-error" role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
            <Button type="submit" data-testid="invite-submit-btn" disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send invitation
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
